import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { WebSocketSubject, webSocket } from "rxjs/webSocket";
import { Store } from "@ngrx/store";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { environment } from "src/environments/environment";

import * as DocumentsActions from "@app/user/documents/documents-store/documents.actions";
import { UserBackendService } from "@app/user/user-core/services/user-backend.service";
import { PendingComponent } from "@shared/pending/pending.component";
import { NCASignResponseDto, nCASign } from "@helper/abstraction/nca-sign";
import { DocumentType } from "@helper/abstraction/documents";
import { DraftDto } from "@helper/abstraction/draft";
import { TranslationService } from "./translation.service";
import { OverlayService } from "./overlay.service";

export type NcaActionType = "BIND" | "SIGN";

export interface NcaActionDto {
	type?: NcaActionType;
	draftType?: any;
	document?: any;
	bindData?: {
		serialNumber?: string;
		xmlBase64?: string;
	}
};

export interface SignDto extends DraftDto {
  method?: string;
  documentType?: DocumentType
}

@Injectable({
	providedIn: "root"
})
export class CryptoSignService {
	public ref?: DynamicDialogRef;
	public unsubscribe$$: Subject<void> = new Subject<void>();

	private env = environment;
	private wssUrl = "wss://127.0.0.1:13579/";
	private wsSubject$$?: WebSocketSubject<any>;

	constructor(
		private store: Store,
		private overlayService: OverlayService,
		private translationService: TranslationService,
		private userBackendService: UserBackendService
	) { }

	public signDocument(signDto: SignDto): void {
		switch (this.env.project) {
			case "KZ":
				this.signNcaCrypto(signDto);
				break;
			case "TK":
				break;
			default:
				this.signCrypto(signDto);
				break;
		}
	}

	private signCrypto(signDto: SignDto): void {
		this.userBackendService.crypto.signD
			.post$(signDto)
			.pipe(takeUntil(this.unsubscribe$$))
			.subscribe(
			signedXml => {
				if (signedXml.ErrorCode !== 0) {
					let errorMessage = signedXml.ErrorMessage;
					if (signedXml.ErrorCode === 1170) errorMessage = this.translationService.getTranslation("wrongUnp");
						this.store.dispatch(DocumentsActions.signDocumentCryptoError(errorMessage, signDto?.documentType));
					} else {
						this.store.dispatch(DocumentsActions.signDocumentCryptoSuccess(signedXml, `${signDto.id}`));
				}
			},
			error => this.store.dispatch(DocumentsActions.signDocumentCryptoError(error, signDto?.documentType)));
	}

	private signNcaCrypto(signDto: SignDto): void {
    	const { xmlBase64, id, documentType, method } = signDto;
		this.openLoadingPopup(true, "SIGN");
		this.wsSubject$$ = webSocket(
				{
					url: this.wssUrl,
					openObserver: {
						next: () => {
							const signData = { ...nCASign, args: { ...nCASign.args, data: xmlBase64 } };
							this.wsSubject$$?.next(signData);
						}
					},
					closeObserver: {
						next: () => {
							this.openLoadingPopup(false);
						}
					}
				}
			);
		this.wsSubject$$.subscribe(
			(res: NCASignResponseDto) => {
				if (res?.body && !Object.keys(res?.body!)?.length) {
					this.openLoadingPopup(false);
				}
				if (res.body?.result?.length) {
					this.openLoadingPopup(false);
					const data = res.body?.result[0]
									.replace(/-----BEGIN CMS-----/g, "")
									.replace(/-----END CMS-----/g, "")
									.replace(/\r\n/g, "")
									.replace(/\n/g, "")
									.trim();
					
					this.store.dispatch(DocumentsActions.signDocumentNcaSuccess(data, String(id), documentType!, method!));
				}
			},
			(error) => {
				this.overlayService?.showNotification$(this.translationService.getTranslation("unavailableNcaLayer"), "error", undefined, undefined, true);
			}
		);
	}

	private openLoadingPopup(checkVersion: boolean, description = "CHECK_VERSION"): void {
		if (checkVersion) {
			this.overlayService.ref = this.overlayService.dialog?.open(PendingComponent, {
				data: { description },
				showHeader: false,
				width: '100%',
				height: '100%'
			});
		} else {
			if (this.overlayService.ref || this.overlayService.dialog) this.overlayService.ref?.close();
		}
	}
}
